
import React, { useState } from 'react';
import MasterLicenseLayout from '@/components/masterlicense/MasterLicenseLayout';
import { useAuth } from '@/context/SupabaseAuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Globe, Plus, Search, Trash2, CheckCircle, XCircle } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

const MasterLicenseDomains: React.FC = () => {
  const { signOut, user } = useAuth();
  const [activeTab, setActiveTab] = useState('registered');
  const [searchQuery, setSearchQuery] = useState('');

  // Sample domain data - would come from an API in a real application
  const domains = [
    { id: '1', domain: 'referme.site', status: 'active', registeredOn: '2023-01-15', expiresOn: '2024-01-15', customDomain: false },
    { id: '2', domain: 'referral-program.com', status: 'active', registeredOn: '2023-02-20', expiresOn: '2024-02-20', customDomain: false },
    { id: '3', domain: 'custom-referrals.net', status: 'pending', registeredOn: '2023-03-10', expiresOn: '2024-03-10', customDomain: true },
    { id: '4', domain: 'myrefprogram.com', status: 'expired', registeredOn: '2022-01-05', expiresOn: '2023-01-05', customDomain: true },
  ];

  const filteredDomains = domains.filter(domain => 
    domain.domain.toLowerCase().includes(searchQuery.toLowerCase()) &&
    (activeTab === 'all' || 
     (activeTab === 'registered' && domain.status === 'active') ||
     (activeTab === 'pending' && domain.status === 'pending') ||
     (activeTab === 'expired' && domain.status === 'expired'))
  );

  return (
    <MasterLicenseLayout 
      isLoading={false}
      onLogout={signOut}
      userRole="Master Admin"
      activeTab="domains"
    >
      <div className="container mx-auto py-8">
        {/* Header */}
        <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-3xl font-bold flex items-center">
              <Globe className="mr-2 h-6 w-6 text-indigo-600" />
              Domain Management
            </h1>
            <p className="text-gray-600 mt-1">
              Manage and monitor domains for the Master License Portal
            </p>
          </div>
          <div className="mt-4 md:mt-0">
            <Button>
              <Plus className="mr-2 h-4 w-4" />
              Add New Domain
            </Button>
          </div>
        </div>

        {/* Domain Settings Card */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Domain Settings</CardTitle>
            <CardDescription>Configure global domain settings for all instances</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="defaultDomain">Default Domain</Label>
                <Input id="defaultDomain" defaultValue="referme.site" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="wildcardDomain">Wildcard Domain</Label>
                <Input id="wildcardDomain" defaultValue="*.referme.site" />
              </div>
            </div>
            
            <div className="flex items-center space-x-2 pt-2">
              <Switch id="allowCustomDomains" defaultChecked />
              <Label htmlFor="allowCustomDomains">Allow custom domains for Super Admins</Label>
            </div>
            
            <div className="pt-4">
              <Button>Save Domain Settings</Button>
            </div>
          </CardContent>
        </Card>

        {/* Domains List */}
        <Card>
          <CardHeader>
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
              <div>
                <CardTitle>Registered Domains</CardTitle>
                <CardDescription>Manage all domains associated with your license</CardDescription>
              </div>
              
              <div className="mt-4 md:mt-0 relative">
                <Search className="absolute left-2 top-3 h-4 w-4 text-gray-400" />
                <Input 
                  placeholder="Search domains..."
                  className="pl-8 w-full md:w-64"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>
          </CardHeader>
          
          <CardContent>
            <Tabs defaultValue="registered" onValueChange={setActiveTab}>
              <TabsList className="mb-4">
                <TabsTrigger value="registered">Registered</TabsTrigger>
                <TabsTrigger value="pending">Pending</TabsTrigger>
                <TabsTrigger value="expired">Expired</TabsTrigger>
                <TabsTrigger value="all">All Domains</TabsTrigger>
              </TabsList>
              
              <TabsContent value={activeTab}>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Domain Name</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Registered On</TableHead>
                      <TableHead>Expires On</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredDomains.length > 0 ? (
                      filteredDomains.map((domain) => (
                        <TableRow key={domain.id}>
                          <TableCell className="font-medium">{domain.domain}</TableCell>
                          <TableCell>
                            {domain.status === 'active' ? (
                              <span className="flex items-center text-green-600">
                                <CheckCircle className="h-4 w-4 mr-1" /> Active
                              </span>
                            ) : domain.status === 'pending' ? (
                              <span className="flex items-center text-amber-600">
                                <svg className="h-4 w-4 mr-1 animate-spin" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                </svg>
                                Pending
                              </span>
                            ) : (
                              <span className="flex items-center text-red-600">
                                <XCircle className="h-4 w-4 mr-1" /> Expired
                              </span>
                            )}
                          </TableCell>
                          <TableCell>{domain.registeredOn}</TableCell>
                          <TableCell>{domain.expiresOn}</TableCell>
                          <TableCell>{domain.customDomain ? 'Custom' : 'Standard'}</TableCell>
                          <TableCell className="text-right">
                            <Button variant="outline" size="sm" className="mr-2">
                              Manage
                            </Button>
                            <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={6} className="h-24 text-center">
                          No domains found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </MasterLicenseLayout>
  );
};

export default MasterLicenseDomains;
